import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { BRANDS as BRAND_IDS } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";

// Mamm's identity is playful, kid-first — big sunny yellow with red pop and a
// hand-lettered wordmark. This bespoke hero replaces BrandHero on the Mamm
// page only, and skews warm and joyful (vs. the editorial dark treatments used
// by Aperina/Desser/Cafeo).
// -----------------------------------------------------------------------------
const SUN = "#FFD53F";
const RED = "#E4004E";
const INK = "#1A0A00";
const BLUE = "#2C8FD9";

export default function MammHero({ brand, inquiryHref }) {
  const { t } = useT();
  const navigate = useNavigate();

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: SUN, color: INK }}
    >
      {/* Ambient wordmark */}
      <img
        aria-hidden
        src={brand.logo}
        alt=""
        className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] max-w-none opacity-[0.06]"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-20 pb-24 lg:pb-32">
        <button
          onClick={() => navigate("/#brands")}
          data-testid={BRAND_IDS.backHome}
          className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-100 transition-opacity mb-10 opacity-70"
          style={{ color: INK }}
        >
          <ArrowLeft className="w-4 h-4" /> {t("brandPage.backHome")}
        </button>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p
              className="text-[0.7rem] md:text-xs font-bold uppercase mb-6"
              style={{ color: RED, letterSpacing: "0.36em" }}
            >
              {t(`brandContent.${brand.slug}.category`)}
            </p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-editorial italic leading-[1.02] text-[clamp(2.75rem,6vw,5.75rem)]"
              style={{ color: INK }}
            >
              &ldquo;{t(`brandContent.${brand.slug}.tagline`)}&rdquo;
            </motion.h1>

            <p
              className="font-editorial italic mt-4 text-xl md:text-2xl"
              style={{ color: RED }}
            >
              {t("brandContent.mamm.heroSub") ||
                "Four cheeky characters. One healthy juice."}
            </p>

            <p
              className="mt-10 max-w-lg text-base md:text-lg leading-[1.75]"
              style={{ color: `${INK}dd` }}
            >
              {brand.story &&
                (t(`brandContent.${brand.slug}.story`) || brand.story)}
            </p>

            <p
              className="mt-8 text-xs font-bold uppercase"
              style={{ color: INK, letterSpacing: "0.3em" }}
            >
              {t("brandContent.mamm.attr1") || "100% fruit juice"}
              <span className="mx-2" style={{ color: RED }}>●</span>
              {t("brandContent.mamm.attr2") || "Vitamin A + C"}
              <span className="mx-2" style={{ color: RED }}>●</span>
              {t("brandContent.mamm.attr3") || "No added sugar"}
            </p>

            <Link
              to={inquiryHref}
              data-testid={BRAND_IDS.ctaInquiry}
              className="mt-12 inline-flex items-center gap-2 rounded-full pl-6 pr-2 py-2 text-sm font-medium transition-transform hover:scale-[1.02] shadow-sm"
              style={{ background: RED, color: SUN }}
            >
              {t("brandPage.partnerCta")}
              <span
                className="inline-flex items-center justify-center w-9 h-9 rounded-full"
                style={{ background: SUN, color: RED }}
              >
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </Link>
          </div>

          {/* Right — Kika monkey character bottle */}
          <div className="lg:col-span-6 relative flex flex-col items-center">
            <motion.img
              initial={{ opacity: 0, y: 30, rotate: -4 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              src="/products/mamm-kika.webp"
              alt="Mamm — Kika the Monkey"
              data-testid={BRAND_IDS.deckImage(brand.slug, 0)}
              className="max-h-[520px] w-auto mx-auto drop-shadow-[0_30px_50px_rgba(228,0,78,0.25)]"
            />

            <div
              className="mt-6 h-px w-24"
              style={{ background: `linear-gradient(90deg, transparent, ${RED}, transparent)` }}
            />

            <p
              className="mt-4 text-xs font-bold uppercase"
              style={{ color: INK, letterSpacing: "0.3em" }}
            >
              {t("brandContent.mamm.skuCount") || "4 Characters"}
              <span className="mx-2" style={{ color: BLUE }}>●</span>
              {t("brandContent.mamm.skuFormat") || "Character Bottle"}
              <span className="mx-2" style={{ color: BLUE }}>●</span>
              {t("brandContent.mamm.skuUse") || "Collectible"}
            </p>
          </div>
        </div>
      </div>

      <div
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${RED} 20%, ${RED} 80%, transparent 95%)`,
        }}
      />
    </section>
  );
}
